import { useCallback, useEffect, useRef } from 'react';
import { setLiveReading } from '@entities/reader-stats';
import { openedFrom } from '@shared/lib/navigation-origin';

import type { Article, Issue } from '@/constants';
import {
	ARTICLE_FITS_SCREEN_DWELL_MS,
	contentOf,
	recordArticleRead,
	recordUnitSeen,
	track,
	type EventContent,
} from '@shared/events';

import {
	createReadingSegment,
	type ReadingExit,
	type ReadingSegment,
} from './reading-segment';

/**
 * The reader's reading events (`improvements/23-appendix-event-catalog.md`).
 * The article renderer reports what it sees through its own callbacks and
 * knows nothing about events; these hooks are where the reader turns that
 * into `track()` calls.
 *
 * Responsive layout: `article_open` / `article_read`. Replica layout:
 * `issue_page_view` / `issue_page_leave`, one per visible page. Never both
 * for the same reading, or its time would be counted twice.
 */

const TOC_PICK_WINDOW_MS = 1000;

/**
 * The first article of a reading has the issue's own origin, narrowed to
 * what `article_open` can say: a favorite opens its article directly, a
 * link from outside is a deep link, and everything else is `other`.
 */
function firstArticleSource(): 'favorite' | 'deeplink' | 'other' {
	const from = openedFrom();
	return from === 'favorite' || from === 'deeplink' ? from : 'other';
}

/** Ends the segment when the tab is hidden, the page is closed or the reader leaves. */
function useSegmentLifecycle<T>(segment: ReadingSegment<T>): void {
	const leaving = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	useEffect(() => {
		// An unmount that is taken back at once is not the reader leaving:
		// React's StrictMode unmounts and remounts every component in dev,
		// which used to end each reading the moment it began and open it again.
		clearTimeout(leaving.current);
		leaving.current = undefined;

		const onVisibility = () => {
			if (document.visibilityState === 'hidden') {
				segment.hide();
			} else {
				segment.show();
			}
		};
		// A page can be closed or frozen without ever being hidden first.
		const onPageHide = () => segment.leave('closed');

		document.addEventListener('visibilitychange', onVisibility);
		window.addEventListener('pagehide', onPageHide);
		return () => {
			document.removeEventListener('visibilitychange', onVisibility);
			window.removeEventListener('pagehide', onPageHide);
			// Unmounting is the reader going back out of the reader, or
			// switching layout. Decided a tick later: see above.
			leaving.current = setTimeout(() => segment.leave('back'), 0);
		};
	}, [segment]);
}

function useConstant<T>(create: () => T): T {
	const ref = useRef<T | null>(null);
	ref.current ??= create();
	return ref.current;
}

/**
 * `issue_open`, once per issue per mount of a reader page. `source` is where
 * the reader came from: the downloaded reader is `downloads`, the first
 * screen of a visit is `deeplink`, and otherwise it is the screen the
 * router left (`@shared/lib/navigation-origin`): home, the favorites
 * drawer, or anything else.
 */
export function useIssueOpenEvent({
	issue,
	language,
	downloaded,
}: {
	issue: Pick<Issue, 'id' | 'name'> | null | undefined;
	language: string;
	downloaded: boolean;
}): void {
	const opened = useRef<string | null>(null);
	const issueId = issue?.id;

	useEffect(() => {
		if (!issue || !issueId || opened.current === issueId) {
			return;
		}
		opened.current = issueId;
		track(
			'issue_open',
			{ source: downloaded ? 'downloads' : openedFrom() },
			contentOf({ issue, language }),
		);
		// One event per issue id; the rest is read when it fires.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [issueId]);
}

interface ArticleSubject {
	issue: Pick<Issue, 'id' | 'name'>;
	article: Article;
	index: number;
	/** How many articles the issue has. */
	total: number;
	language: string;
	/** The furthest the reader scrolled, 0 to 1. */
	maxDepth: number;
	/** The article fits the screen: there is nothing to scroll. */
	fitsScreen: boolean;
}

interface ProgressReport {
	articleId: string;
	progress: number;
	/** Absent from a host that does not say: taken as scrollable. */
	scrollable?: boolean;
}

function applyProgress(subject: ArticleSubject, report: ProgressReport): void {
	subject.fitsScreen = report.scrollable === false;
	if (!subject.fitsScreen) {
		subject.maxDepth = Math.max(subject.maxDepth, report.progress);
	}
}

/**
 * How far the article was read in a stretch of `dwellMs`. One that fits the
 * screen has no scroll position to go by, so staying on it decides
 * (`ARTICLE_FITS_SCREEN_DWELL_MS`).
 */
function depthOf(subject: ArticleSubject, dwellMs: number): number {
	return subject.fitsScreen && dwellMs >= ARTICLE_FITS_SCREEN_DWELL_MS
		? 1
		: subject.maxDepth;
}

/**
 * Notes a page or an article as seen and records `issue_complete` the first
 * time the issue's share of them reaches the rule. `units_total` is the
 * issue's size as this device saw it.
 */
function noteUnitSeen(
	content: EventContent,
	layout: 'replica' | 'responsive',
	unit: number,
	total: number,
): void {
	const issueId = content.issue_id;
	if (issueId === undefined || total <= 0) {
		return;
	}
	void recordUnitSeen({ issueId, layout, unit, total }).then(
		({ completed, seen }) => {
			if (completed) {
				track(
					'issue_complete',
					{ units_seen: seen, units_total: total },
					{
						issue_id: issueId,
						issue_title: content.issue_title,
						content_language: content.content_language,
						content_type: layout,
					},
				);
			}
		},
	);
}

/**
 * Responsive layout. Call with the article on screen; pass the returned
 * function to the renderer as `onReadingProgress`.
 */
export function useArticleReadingEvents({
	issue,
	article,
	index,
	total,
	language,
	isTocOpen,
}: {
	issue: Pick<Issue, 'id' | 'name'> | null | undefined;
	article: Article | null | undefined;
	index: number | null;
	/** How many articles the issue has. */
	total: number;
	language: string;
	isTocOpen: boolean;
}): (progress: ProgressReport) => void {
	const first = useRef(true);
	// The renderer reports an article's starting position once, and can do so
	// a frame before the reading of that article has started here (the
	// article on screen is set in an effect, a render behind the index). An
	// article that is never scrolled gets no second report, so one that
	// arrives early is kept for the reading it belongs to.
	const early = useRef<ProgressReport | null>(null);
	// Picking an article closes the drawer in the same tick, before the new
	// article is on screen, so "the drawer was open a moment ago" is the
	// signal, not "it is open now".
	const tocOpenUntil = useRef(0);
	const tocWasOpen = useRef(false);
	useEffect(() => {
		if (isTocOpen) {
			tocWasOpen.current = true;
			tocOpenUntil.current = Infinity;
		} else if (tocWasOpen.current) {
			tocOpenUntil.current = Date.now() + TOC_PICK_WINDOW_MS;
		}
	}, [isTocOpen]);

	const segment = useConstant(() =>
		createReadingSegment<ArticleSubject>({
			onEnter: (subject) => {
				const source = first.current
					? firstArticleSource()
					: Date.now() <= tocOpenUntil.current
						? 'toc'
						: 'swipe';
				first.current = false;
				const content = contentOfArticle(subject);
				track(
					'article_open',
					{ source, units_total: subject.total > 0 ? subject.total : undefined },
					content,
				);
				noteUnitSeen(content, 'responsive', subject.index + 1, subject.total);
			},
			onLeave: (subject, dwellMs, exit) => {
				const content = contentOfArticle(subject);
				const depth = Math.round(depthOf(subject, dwellMs) * 100) / 100;
				track(
					'article_read',
					{ dwell_ms: dwellMs, max_scroll_depth: depth, exit },
					content,
				);
				const { issue_id: issueId, article_id: articleId } = content;
				if (issueId === undefined || articleId === undefined) {
					return;
				}
				void recordArticleRead({ issueId, articleId, dwellMs, depth }).then(
					({ completed, dwellMs: totalMs }) => {
						if (completed) {
							track('article_complete', { dwell_ms: totalMs }, content);
						}
					},
				);
			},
		}),
	);
	useSegmentLifecycle(segment);
	// For a stats view opened while the article is still being read.
	useEffect(() => {
		setLiveReading(() => {
			const subject = segment.current();
			return subject
				? {
						articleId: contentOfArticle(subject).article_id,
						elapsedMs: segment.elapsed(),
						depth: depthOf(subject, segment.elapsed()),
					}
				: undefined;
		});
		return () => setLiveReading(null);
	}, [segment]);

	const articleId = article?.id;
	useEffect(() => {
		if (!issue || !article || index === null) {
			return;
		}
		const previous = segment.current();
		if (previous?.article.id === article.id) {
			return;
		}
		const exit: ReadingExit =
			previous && index < previous.index ? 'back' : 'next';
		const subject: ArticleSubject = {
			issue,
			article,
			index,
			total,
			language,
			maxDepth: 0,
			fitsScreen: false,
		};
		if (early.current?.articleId === article.id) {
			applyProgress(subject, early.current);
		}
		early.current = null;
		segment.enter(subject, exit);
		// Keyed on the article: `issue` and `article` are new objects on most
		// renders and must not restart a reading.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [articleId, index, segment]);

	return useCallback(
		(report) => {
			const subject = segment.current();
			if (subject && subject.article.id === report.articleId) {
				applyProgress(subject, report);
			} else {
				early.current = report;
			}
		},
		[segment],
	);
}

function contentOfArticle({ issue, article, index, language }: ArticleSubject) {
	return contentOf({
		issue,
		article,
		language,
		contentType: 'responsive',
		// The article's position in the issue, counted from 1 like a page.
		pageNumber: index + 1,
	});
}

export interface ReplicaPageOnScreen {
	/** Counted from 1. */
	pageNumber: number;
	/** The article the page belongs to, when it belongs to one. */
	article?: Article | null;
}

interface PagesSubject {
	issue: Pick<Issue, 'id' | 'name'>;
	pages: Array<ReplicaPageOnScreen>;
	totalPages: number;
	language: string;
	key: string;
	first: number;
}

/**
 * Replica layout. Call with the pages on screen: one in single-page view,
 * one or two in double-page view. Each visible page gets its own
 * `issue_page_view` and `issue_page_leave`, with `pages_visible` saying how
 * many shared the screen (both report the full time; the reading side
 * divides).
 */
export function useReplicaReadingEvents({
	issue,
	pages,
	totalPages,
	language,
}: {
	issue: Pick<Issue, 'id' | 'name'> | null | undefined;
	pages: Array<ReplicaPageOnScreen>;
	/** How many pages the issue has. */
	totalPages: number;
	language: string;
}): void {
	const segment = useConstant(() =>
		createReadingSegment<PagesSubject>({
			onEnter: (subject) => {
				for (const page of subject.pages) {
					const content = contentOfPage(subject, page);
					track(
						'issue_page_view',
						{
							pages_visible: visibleCount(subject),
							units_total:
								subject.totalPages > 0 ? subject.totalPages : undefined,
						},
						content,
					);
					noteUnitSeen(content, 'replica', page.pageNumber, subject.totalPages);
				}
			},
			onLeave: (subject, dwellMs) => {
				for (const page of subject.pages) {
					track(
						'issue_page_leave',
						{ dwell_ms: dwellMs, pages_visible: visibleCount(subject) },
						contentOfPage(subject, page),
					);
				}
			},
		}),
	);
	useSegmentLifecycle(segment);
	useEffect(() => {
		setLiveReading(() =>
			segment.current() ? { elapsedMs: segment.elapsed() } : undefined,
		);
		return () => setLiveReading(null);
	}, [segment]);

	const key = pages.map((page) => page.pageNumber).join(',');
	useEffect(() => {
		if (!issue || !pages.length) {
			return;
		}
		const previous = segment.current();
		if (previous?.key === key) {
			return;
		}
		const first = pages[0].pageNumber;
		segment.enter(
			{ issue, pages, totalPages, language, key, first },
			previous && first < previous.first ? 'back' : 'next',
		);
		// Keyed on the page numbers: `pages` is a new array on every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key, segment]);
}

function visibleCount(subject: PagesSubject): 1 | 2 {
	return subject.pages.length > 1 ? 2 : 1;
}

function contentOfPage(subject: PagesSubject, page: ReplicaPageOnScreen) {
	return contentOf({
		issue: subject.issue,
		article: page.article,
		language: subject.language,
		contentType: 'replica',
		pageNumber: page.pageNumber,
	});
}
